import React, { useMemo } from 'react'
import {
    Button,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
    useTheme,
} from '@material-ui/core'
import { Container } from '../../components/Container'
import { TurnProjectDetailRow } from '../../hooks/useKPI'
import {
    DEFAULT_CAI_PASS_THRESHOLD,
    DEFAULT_TABLE_FILTERS,
    hasActiveTableFilters,
    meetsCaiThreshold,
    PRIMARY_METHOD_LABELS,
    SortDirection,
    SortField,
    TableFilters,
} from './turnProjectsConstants'

type Props = {
    rows: TurnProjectDetailRow[]
    caiPassThreshold?: number
    filters: TableFilters
    onFiltersChange: (filters: TableFilters) => void
    showSourceBreakdown: boolean
    onShowSourceBreakdownChange: (value: boolean) => void
    sortField: SortField
    sortDirection: SortDirection
    onSortChange: (field: SortField) => void
    page: number
    rowsPerPage: number
    onPageChange: (page: number) => void
    onRowsPerPageChange: (rowsPerPage: number) => void
    yearOptions: number[]
}

const compareRows = (
    a: TurnProjectDetailRow,
    b: TurnProjectDetailRow,
    sortField: SortField,
    sortDirection: SortDirection,
    thresholdPercent: number,
): number => {
    const direction = sortDirection === 'asc' ? 1 : -1

    const getValue = (row: TurnProjectDetailRow): string | number => {
        switch (sortField) {
            case 'apartment_name':
                return row.apartment_name.toLowerCase()
            case 'year':
                return row.year ?? -1
            case 'schedule_name':
                return row.schedule_name.toLowerCase()
            case 'primary_method':
                return (
                    PRIMARY_METHOD_LABELS[row.primary_method] ??
                    row.primary_method
                ).toLowerCase()
            case 'total':
                return row.total
            case 'cai_count':
                return row.cai_count
            case 'cai_rate':
                return row.cai_rate
            case 'met_threshold':
                return meetsCaiThreshold(row.cai_rate, thresholdPercent) ? 1 : 0
            default:
                return 0
        }
    }

    const aValue = getValue(a)
    const bValue = getValue(b)

    if (aValue < bValue) {
        return -1 * direction
    }
    if (aValue > bValue) {
        return 1 * direction
    }
    return 0
}

const computeFilteredSummary = (
    rows: TurnProjectDetailRow[],
    thresholdPercent: number,
) => {
    if (rows.length === 0) {
        return {
            count: 0,
            avgCaiRate: 0,
            pooledCaiRate: 0,
            passRate: 0,
        }
    }

    const totalWos = rows.reduce((sum, row) => sum + row.total, 0)
    const totalCai = rows.reduce((sum, row) => sum + row.cai_count, 0)
    const passed = rows.filter((row) =>
        meetsCaiThreshold(row.cai_rate, thresholdPercent),
    ).length

    return {
        count: rows.length,
        avgCaiRate:
            (rows.reduce((sum, row) => sum + row.cai_rate, 0) / rows.length) *
            100,
        pooledCaiRate: totalWos > 0 ? (totalCai / totalWos) * 100 : 0,
        passRate: (passed / rows.length) * 100,
    }
}

export const TurnProjectsTable = ({
    rows,
    caiPassThreshold = DEFAULT_CAI_PASS_THRESHOLD,
    filters,
    onFiltersChange,
    showSourceBreakdown,
    onShowSourceBreakdownChange,
    sortField,
    sortDirection,
    onSortChange,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    yearOptions,
}: Props) => {
    const theme = useTheme()
    const filtersActive = hasActiveTableFilters(filters)

    const filteredRows = useMemo(() => {
        const search = filters.apartmentSearch.trim().toLowerCase()
        return rows.filter((row) => {
            if (
                filters.primaryMethod !== 'ALL' &&
                row.primary_method !== filters.primaryMethod
            ) {
                return false
            }
            if (filters.year !== 'ALL' && row.year !== filters.year) {
                return false
            }
            if (search && !row.apartment_name.toLowerCase().includes(search)) {
                return false
            }
            const passed = meetsCaiThreshold(row.cai_rate, caiPassThreshold)
            if (filters.caiThreshold === 'PASS' && !passed) {
                return false
            }
            if (filters.caiThreshold === 'FAIL' && passed) {
                return false
            }
            return true
        })
    }, [rows, filters, caiPassThreshold])

    const sortedRows = useMemo(
        () =>
            [...filteredRows].sort((a, b) =>
                compareRows(a, b, sortField, sortDirection, caiPassThreshold),
            ),
        [filteredRows, sortField, sortDirection, caiPassThreshold],
    )

    const paginatedRows = useMemo(() => {
        const start = page * rowsPerPage
        return sortedRows.slice(start, start + rowsPerPage)
    }, [sortedRows, page, rowsPerPage])

    const summary = useMemo(
        () => computeFilteredSummary(filteredRows, caiPassThreshold),
        [filteredRows, caiPassThreshold],
    )

    const updateFilters = (partial: Partial<TableFilters>) => {
        onFiltersChange({ ...filters, ...partial })
        onPageChange(0)
    }

    const renderSortLabel = (label: string, field: SortField) => (
        <TableSortLabel
            active={sortField === field}
            direction={sortField === field ? sortDirection : 'asc'}
            onClick={() => onSortChange(field)}
        >
            {label}
        </TableSortLabel>
    )

    const filterChipText = [
        filters.primaryMethod !== 'ALL'
            ? PRIMARY_METHOD_LABELS[filters.primaryMethod] ??
              filters.primaryMethod
            : null,
        filters.year !== 'ALL' ? String(filters.year) : null,
        filters.apartmentSearch.trim()
            ? `"${filters.apartmentSearch.trim()}"`
            : null,
        filters.caiThreshold === 'PASS'
            ? `≥${caiPassThreshold}% CAI`
            : filters.caiThreshold === 'FAIL'
            ? `<${caiPassThreshold}% CAI`
            : null,
    ]
        .filter(Boolean)
        .join(' · ')

    return (
        <Container
            style={{
                display: 'grid',
                flex: 1,
                gridTemplateRows: 'auto minmax(0, 1fr) auto',
                minHeight: 0,
                minWidth: 0,
                overflow: 'hidden',
                width: '100%',
            }}
        >
            <Container
                style={{
                    flexDirection: 'column',
                    minWidth: 0,
                }}
            >
                <Container
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        gap: theme.spacing(1.5),
                        marginBottom: theme.spacing(2),
                        alignItems: 'center',
                    }}
                >
                    <FormControl
                        size="small"
                        variant="outlined"
                        style={{ minWidth: 160 }}
                    >
                        <InputLabel>Primary</InputLabel>
                        <Select
                            label="Primary"
                            value={filters.primaryMethod}
                            onChange={(e) =>
                                updateFilters({
                                    primaryMethod: e.target.value as
                                        | string
                                        | 'ALL',
                                })
                            }
                        >
                            <MenuItem value="ALL">All</MenuItem>
                            {Object.entries(PRIMARY_METHOD_LABELS).map(
                                ([key, label]) => (
                                    <MenuItem key={key} value={key}>
                                        {label}
                                    </MenuItem>
                                ),
                            )}
                        </Select>
                    </FormControl>

                    <FormControl
                        size="small"
                        variant="outlined"
                        style={{ minWidth: 100 }}
                    >
                        <InputLabel>Year</InputLabel>
                        <Select
                            label="Year"
                            value={filters.year}
                            onChange={(e) => {
                                const value = e.target.value
                                updateFilters({
                                    year:
                                        value === 'ALL' ? 'ALL' : Number(value),
                                })
                            }}
                        >
                            <MenuItem value="ALL">All</MenuItem>
                            {yearOptions.map((year) => (
                                <MenuItem key={year} value={year}>
                                    {year}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        size="small"
                        variant="outlined"
                        label="Apartment"
                        value={filters.apartmentSearch}
                        onChange={(e) =>
                            updateFilters({ apartmentSearch: e.target.value })
                        }
                        style={{ minWidth: 180 }}
                    />

                    <FormControl
                        size="small"
                        variant="outlined"
                        style={{ minWidth: 150 }}
                    >
                        <InputLabel>CAI Status</InputLabel>
                        <Select
                            label="CAI Status"
                            value={filters.caiThreshold}
                            onChange={(e) =>
                                updateFilters({
                                    caiThreshold: e.target
                                        .value as TableFilters['caiThreshold'],
                                })
                            }
                        >
                            <MenuItem value="ALL">All</MenuItem>
                            <MenuItem value="PASS">
                                Met threshold (≥{caiPassThreshold}%)
                            </MenuItem>
                            <MenuItem value="FAIL">Below threshold</MenuItem>
                        </Select>
                    </FormControl>

                    {filtersActive && (
                        <Button
                            size="small"
                            onClick={() => {
                                onFiltersChange({ ...DEFAULT_TABLE_FILTERS })
                                onPageChange(0)
                            }}
                        >
                            Clear filters
                        </Button>
                    )}

                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={showSourceBreakdown}
                                onChange={(_, checked) =>
                                    onShowSourceBreakdownChange(checked)
                                }
                            />
                        }
                        label="Show WO source breakdown"
                    />
                </Container>

                {filtersActive && (
                    <Container
                        style={{
                            marginBottom: theme.spacing(1.5),
                            gap: theme.spacing(3),
                            flexWrap: 'wrap',
                        }}
                    >
                        {filterChipText && (
                            <Typography variant="body2" color="textSecondary">
                                Filtered: {filterChipText}
                            </Typography>
                        )}
                        <Typography variant="body2">
                            <strong>{summary.count}</strong> projects
                        </Typography>
                        <Typography variant="body2">
                            Avg CAI:{' '}
                            <strong>{summary.avgCaiRate.toFixed(1)}%</strong>
                        </Typography>
                        <Typography variant="body2">
                            Pooled CAI:{' '}
                            <strong>{summary.pooledCaiRate.toFixed(1)}%</strong>
                        </Typography>
                        <Typography variant="body2">
                            ≥{caiPassThreshold}% CAI:{' '}
                            <strong>{summary.passRate.toFixed(1)}%</strong>
                        </Typography>
                    </Container>
                )}
            </Container>

            <Container
                style={{
                    flex: 1,
                    flexDirection: 'column',
                    minHeight: 0,
                    minWidth: 0,
                    overflow: 'hidden',
                }}
            >
                <TableContainer
                    style={{
                        flex: 1,
                        minHeight: 0,
                        minWidth: 0,
                        maxWidth: '100%',
                        overflowX: 'auto',
                        overflowY: 'auto',
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        style={{
                            minWidth: showSourceBreakdown ? 1200 : 800,
                            width: 'max-content',
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    {renderSortLabel(
                                        'Apartment',
                                        'apartment_name',
                                    )}
                                </TableCell>
                                <TableCell>
                                    {renderSortLabel('Year', 'year')}
                                </TableCell>
                                <TableCell>
                                    {renderSortLabel(
                                        'Project',
                                        'schedule_name',
                                    )}
                                </TableCell>
                                <TableCell>
                                    {renderSortLabel(
                                        'Primary',
                                        'primary_method',
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    {renderSortLabel('Total', 'total')}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    title="Completed, Approved & Invoiced"
                                >
                                    {renderSortLabel('CAI', 'cai_count')}
                                </TableCell>
                                <TableCell align="right">
                                    {renderSortLabel('CAI Rate', 'cai_rate')}
                                </TableCell>
                                <TableCell align="center">
                                    {renderSortLabel(
                                        'Met Threshold',
                                        'met_threshold',
                                    )}
                                </TableCell>
                                {showSourceBreakdown && (
                                    <>
                                        <TableCell align="right">
                                            Auto
                                        </TableCell>
                                        <TableCell align="right">
                                            Step Lori
                                        </TableCell>
                                        <TableCell align="right">
                                            Adv Lori
                                        </TableCell>
                                        <TableCell align="right">
                                            Adv Lori (Mob)
                                        </TableCell>
                                        <TableCell align="right">
                                            Bulk
                                        </TableCell>
                                        <TableCell align="right">
                                            Walk
                                        </TableCell>
                                        <TableCell align="right">
                                            Assign
                                        </TableCell>
                                    </>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedRows.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={showSourceBreakdown ? 15 : 8}
                                        align="center"
                                    >
                                        No projects match the current filters.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedRows.map((row) => {
                                    const metThreshold = meetsCaiThreshold(
                                        row.cai_rate,
                                        caiPassThreshold,
                                    )
                                    return (
                                        <TableRow key={row.schedule_id}>
                                            <TableCell>
                                                {row.apartment_name}
                                            </TableCell>
                                            <TableCell>
                                                {row.year ?? '—'}
                                            </TableCell>
                                            <TableCell>
                                                {row.schedule_name}
                                            </TableCell>
                                            <TableCell>
                                                {PRIMARY_METHOD_LABELS[
                                                    row.primary_method
                                                ] ?? row.primary_method}
                                            </TableCell>
                                            <TableCell align="right">
                                                {row.total}
                                            </TableCell>
                                            <TableCell align="right">
                                                {row.cai_count}
                                            </TableCell>
                                            <TableCell align="right">
                                                {(row.cai_rate * 100).toFixed(
                                                    1,
                                                )}
                                                %
                                            </TableCell>
                                            <TableCell align="center">
                                                {metThreshold ? 'Yes' : 'No'}
                                            </TableCell>
                                            {showSourceBreakdown && (
                                                <>
                                                    <TableCell align="right">
                                                        {row.auto_scheduler_web}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {row.lori_step_web}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {row.lori_advanced_web}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {
                                                            row.lori_advanced_mobile
                                                        }
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {row.bulk_assign_web}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {row.walk_scheduler_web}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {row.assign}
                                                    </TableCell>
                                                </>
                                            )}
                                        </TableRow>
                                    )
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Container>

            <TablePagination
                component="div"
                style={{ flexShrink: 0 }}
                count={sortedRows.length}
                page={page}
                onChangePage={(
                    _event: React.MouseEvent<HTMLButtonElement> | null,
                    newPage: number,
                ) => onPageChange(newPage)}
                rowsPerPage={rowsPerPage}
                onChangeRowsPerPage={(
                    event: React.ChangeEvent<HTMLInputElement>,
                ) => {
                    onRowsPerPageChange(parseInt(event.target.value, 10))
                    onPageChange(0)
                }}
                rowsPerPageOptions={[25, 50, 100]}
                labelDisplayedRows={({ from, to, count }) => {
                    const base = `${from}-${to} of ${count}`
                    if (count < rows.length) {
                        return `${base} (of ${rows.length} total)`
                    }
                    return base
                }}
            />
        </Container>
    )
}
